import { useEffect, useState } from "react";
import { formatDate } from "../../../_app/utils/format";
import FilterBox from "./FilterBox";
import { isBetween } from "../../../_app/utils";
import { createStylesheet } from "../../../_app/utils/styles";
import { AdapterDateFns, LocalizationProvider, PickersDay, PickersDayProps, StaticDatePicker } from "../../../_app/components";

// MUI X v5 fires the (required) `onChange` prop with a partial value whenever a year or month is
// picked in the calendar header, before the user has chosen a day. Committing that partial value
// used to lock the "From" date to the current day of the chosen year (the range switched to the "To"
// picker with that day as its minimum, so earlier months and days became unreachable). A completed
// day selection is reported through `onAccept`, so that is what the range handlers listen to.
const ignoreIntermediateChange = () => undefined;

export default function DateRangeFilter({ item, addSelection, selection, setSelection, close, resetSelection, ...props }: any) {
  const PATTERN: string = "dd/MM/yyyy";

  const [showFromPicker, setShowFromPicker] = useState(true);
  const [dateSelectedFrom, setDateSelectedFrom] = useState<Date | null>(null);

  const [showToPicker, setShowToPicker] = useState(false);
  const [dateSelectedTo, setDateSelectedTo] = useState<Date | null>(null);
  const classes = useStyles();

  useEffect(() => {
    if (!dateSelectedFrom || !dateSelectedTo) {
      setSelection([]);
      return;
    }

    setSelection([formatDateValue(dateSelectedFrom), formatDateValue(dateSelectedTo)]);
  }, [setSelection, dateSelectedTo, dateSelectedFrom]);

  const formatDateValue = (date: Date) => formatDate(new Date(date).toISOString(), PATTERN);

  const handleFromDateAccept = (date: Date | null) => {
    if (!date) return;
    setDateSelectedFrom(date);
    setDateSelectedTo(date);
    setShowFromPicker(false);
    setShowToPicker(true);
  };

  const handleToDateAccept = (date: Date | null) => {
    if (!date) return;
    setDateSelectedTo(date);
  };

  const handleReset = () => {
    setDateSelectedFrom(null);
    setDateSelectedTo(null);
    setShowFromPicker(true);
    setShowToPicker(false);
  };

  return (
    <div {...props}>
      <FilterBox
        item={item}
        resetSelection={handleReset}
        addSelection={addSelection}
        selection={selection}
        close={close}
        showReset
      >
        <div className={classes.filterBody}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <>
              {showFromPicker && (
                <StaticDatePicker
                  maxDate={new Date()}
                  inputFormat={PATTERN}
                  label="From"
                  renderInput={(props: any) => <div {...props} />}
                  value={dateSelectedFrom}
                  onChange={ignoreIntermediateChange}
                  onAccept={handleFromDateAccept}
                  disableHighlightToday
                  displayStaticWrapperAs="desktop"
                />
              )}
              {showToPicker && (
                <StaticDatePicker
                  minDate={dateSelectedFrom ?? undefined}
                  inputFormat={PATTERN}
                  label="To"
                  renderInput={(props: any) => <div />}
                  value={dateSelectedTo}
                  onChange={ignoreIntermediateChange}
                  onAccept={handleToDateAccept}
                  disableHighlightToday
                  displayStaticWrapperAs="desktop"
                  renderDay={(day: Date, selectedDays: (Date | null)[], pickersDayProps: PickersDayProps<Date>) => {
                    const selected = selectedDays[0];
                    const { key, ...props } = pickersDayProps;
                    return (
                      <PickersDay
                        key={key}
                        {...props}
                        selected={!!(day && selected && dateSelectedFrom && isBetween(day, dateSelectedFrom, selected))}
                      />
                    );
                  }}
                />
              )}
            </>
          </LocalizationProvider>
        </div>
      </FilterBox>
    </div>
  );
}

const useStyles = createStylesheet((theme) => ({
  filterBody: {
    margin: "0px 0px",
    "& .MuiPickersCalendarHeader-root": {
      marginTop: 0,
    },
  },
}));
